import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import type { ReactNode } from "react";
import type { SidebarTab } from "@/components/panels/left-sidebar";
import { settingsStackFor, type SettingsTarget } from "@/components/settings/settings-navigation";
import type { TrayPanelRect } from "@/desktop/tray-panel-geometry";
import type { WindowOpener } from "@/desktop/window-opener";
import { isTauri } from "@/lib/is-tauri";
import { safeTauriUnlisten } from "@/desktop/tauri-listener-cleanup";
import { useDesktopMode } from "@/stores/desktop-mode-store";
import {
  openSettingsFrame,
  closeSettingsFrame,
  setSettingsFrameStack,
  useSettingsFrameStore,
} from "@/desktop/settings-frame-store";
import { getWindowDisplayId } from "@/desktop/use-window-role";

/**
 * Layout context — manages sidebar and settings state.
 */

const ROOT_STACK: readonly SettingsTarget[] = [{ page: "root" }];

const STORAGE_KEY_SIDEBAR = "denker.layout.sidebarOpen";
const ONBOARDING_RIGHT_PANEL_EVENT = "denker://onboarding-right-panel";

function readBool(key: string, fallback: boolean): boolean {
  if (typeof window === "undefined") return fallback;
  try {
    const stored = window.localStorage.getItem(key);
    if (stored === "true") return true;
    if (stored === "false") return false;
  } catch {
    // localStorage may throw in private mode / disabled storage — fall through.
  }
  return fallback;
}

function writeBool(key: string, value: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // ignore quota / disabled-storage errors
  }
}

export interface LayoutContextValue {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  sidebarTab: SidebarTab;
  setSidebarTab: (tab: SidebarTab) => void;
  /** The person opens the settings modal to a specific tab. */
  openSettings: (target: SettingsTarget) => void;
  settingsOpen: boolean;
  /** The person opened the open Settings, so it may take the keyboard. */
  settingsTakesKeyboard: boolean;
  settingsStack: readonly SettingsTarget[];
  setSettingsStack: (stack: readonly SettingsTarget[]) => void;
  closeSettings: () => void;
}

const LayoutContext = createContext<LayoutContextValue | null>(null);

export function useLayout(): LayoutContextValue {
  const ctx = useContext(LayoutContext);
  if (!ctx) {
    throw new Error("useLayout must be used within LayoutProvider");
  }
  return ctx;
}

interface LayoutProviderProps {
  children: ReactNode;
  defaultSidebarOpen?: boolean;
}

export function LayoutProvider({
  children,
  defaultSidebarOpen = false,
}: LayoutProviderProps) {
  // Lazy-init from localStorage so reloads honor the user's last choice.
  // The `default*Open` props remain the initial-paint fallback when no
  // saved value exists.
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    readBool(STORAGE_KEY_SIDEBAR, defaultSidebarOpen),
  );

  // Persist on every change. Cheap (one localStorage write) and avoids a
  // separate setter wrapper that would have to be threaded through
  // every consumer.
  useEffect(() => {
    writeBool(STORAGE_KEY_SIDEBAR, sidebarOpen);
  }, [sidebarOpen]);
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>("frames");
  const [webSettingsOpen, setSettingsOpen] = useState(false);
  const [webSettingsStack, setWebSettingsStack] = useState<readonly SettingsTarget[]>(ROOT_STACK);
  const ownsSettingsFrame = useSettingsFrameStore(
    (state) => state.frame?.displayId === getWindowDisplayId(),
  );
  const desktopSettingsStack = useSettingsFrameStore(
    (state) => state.frame?.stack ?? ROOT_STACK,
  );
  const desktopSettingsOpenedByUser = useSettingsFrameStore(
    (state) => state.frame?.openedBy === "user",
  );
  const settingsOpen = isTauri()
    ? ownsSettingsFrame
    : webSettingsOpen;
  const settingsStack = isTauri()
    ? desktopSettingsStack
    : webSettingsStack;
  // Only the desktop lets an agent open Settings; on the web the person did.
  const settingsTakesKeyboard = isTauri() ? desktopSettingsOpenedByUser : true;

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  const openSettingsFor = useCallback(
    (
      target: SettingsTarget,
      opener: WindowOpener,
      trayRect?: TrayPanelRect,
    ) => {
      if (isTauri()) {
        void openSettingsFrame(target, opener, trayRect);
        return;
      }
      setWebSettingsStack(settingsStackFor(target, "web"));
      setSettingsOpen(true);
    },
    [],
  );
  const openSettings = useCallback(
    (target: SettingsTarget) => openSettingsFor(target, "user"),
    [openSettingsFor],
  );

  const setSettingsStack = useCallback((stack: readonly SettingsTarget[]) => {
    if (isTauri()) setSettingsFrameStack([...stack]);
    else setWebSettingsStack(stack);
  }, []);

  const pendingSettingsRequest = useDesktopMode((state) => state.pendingSettingsRequest);
  useEffect(() => {
    if (!pendingSettingsRequest) return;
    const request = useDesktopMode.getState().consumeSettingsRequest();
    if (request)
      openSettingsFor(request.target, request.opener, request.trayRect);
  }, [pendingSettingsRequest, openSettingsFor]);

  const closeSettings = useCallback(() => {
    if (isTauri()) closeSettingsFrame();
    setSettingsOpen(false);
  }, []);

  useEffect(() => {
    type RightPanelDetail = {
      open?: boolean;
      clearSelectedTask?: boolean;
      selectedTaskId?: string | null;
      closeSettings?: boolean;
    };

    const applyDetail = async (detail: RightPanelDetail | undefined) => {
      if (detail?.closeSettings) {
        closeSettings();
      }
      if (
        detail?.clearSelectedTask ||
        Object.prototype.hasOwnProperty.call(detail ?? {}, "selectedTaskId")
      ) {
        const { useTaskPocketStore } = await import("@/stores/task-pocket-store");
        if (detail?.clearSelectedTask || !detail?.selectedTaskId) useTaskPocketStore.getState().close();
        else {
          const { openTaskDetails } = await import("@/lib/right-panel-navigation");
          await openTaskDetails(detail.selectedTaskId);
        }
      }
    };

    const handler = (e: Event) => {
      void applyDetail((e as CustomEvent<RightPanelDetail>).detail);
    };
    window.addEventListener("denker:right-panel", handler);
    let unlisten: (() => void) | null = null;
    let cancelled = false;
    if (isTauri()) {
      void import("@tauri-apps/api/event")
        .then(({ listen }) =>
          listen<RightPanelDetail>(ONBOARDING_RIGHT_PANEL_EVENT, (e) => {
            void applyDetail(e.payload);
          }),
        )
        .then((u) => {
          if (cancelled) safeTauriUnlisten(u);
          else unlisten = u;
        })
        .catch(() => {});
    }
    return () => {
      cancelled = true;
      window.removeEventListener("denker:right-panel", handler);
      if (unlisten) safeTauriUnlisten(unlisten);
    };
  }, [closeSettings]);

  const value = useMemo<LayoutContextValue>(
    () => ({
      sidebarOpen,
      setSidebarOpen,
      toggleSidebar,
      sidebarTab,
      setSidebarTab,
      openSettings,
      settingsOpen,
      settingsTakesKeyboard,
      settingsStack,
      setSettingsStack,
      closeSettings,
    }),
    [
      sidebarOpen,
      sidebarTab,
      setSidebarOpen,
      setSidebarTab,
      toggleSidebar,
      openSettings,
      settingsOpen,
      settingsTakesKeyboard,
      settingsStack,
      setSettingsStack,
      closeSettings,
    ],
  );

  return (
    <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
  );
}
